import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from '../../../components/Typography/Typography';
import Modal from '../../../components/UI/Modal/Modal';
import Button from '../../../components/UI/Button/Button';
import BenchmarkQuestionForm from './components/BenchmarkQuestionForm';
import BenchmarkHeader from './components/BenchmarkHeader';
import BenchmarkFilters from './components/BenchmarkFilters';
import BenchmarkGrid from './components/BenchmarkGrid';
import { RootState } from '../../../store';
import {
  setLoading,
  setError,
  setBenchmarkData,
  deleteQuestion,
} from '../../../store/benchmark/slice';
import {
  getBenchmarkQuestions,
  updateBenchmarkQuestion,
  deleteBenchmarkQuestion,
} from '../../../api/benchmarkApi';
import { BenchmarkQuestion } from '../../../store/benchmark/types';
import styles from './BenchmarkQuestionsPage.module.scss';

const BenchmarkQuestionsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [editingQuestion, setEditingQuestion] = useState<BenchmarkQuestion | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState<BenchmarkQuestion | null>(null);

  const {
    questions,
    loading,
    error,
    filters: { query_type, user_persona },
  } = useSelector((state: RootState) => state.benchmark);

  useEffect(() => {
    fetchQuestions();
  }, [query_type, user_persona]);

  const fetchQuestions = async () => {
    try {
      dispatch(setLoading(true));
      const data = await getBenchmarkQuestions({
        query_type,
        user_persona,
      });
      dispatch(setBenchmarkData(data));
      dispatch(setError(null));
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : 'An error occurred'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleUpdate = async (values: Partial<BenchmarkQuestion>) => {
    if (!editingQuestion) return;

    try {
      await updateBenchmarkQuestion(editingQuestion.id, values);
      setEditingQuestion(null);
      fetchQuestions();
    } catch (err) {
      console.error('Error updating question:', err);
    }
  };

  const handleDelete = async () => {
    if (!deletingQuestion) return;

    try {
      await deleteBenchmarkQuestion(deletingQuestion.id);
      dispatch(deleteQuestion(deletingQuestion.id));
      setDeletingQuestion(null);
    } catch (err) {
      console.error('Error deleting question:', err);
    }
  };

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <Text color="red">Error: {error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <BenchmarkHeader
        loading={loading}
        setError={(error) => dispatch(setError(error))}
        onQuestionCreated={fetchQuestions}
      />

      <BenchmarkFilters />

      {loading ? (
        <div className={styles.loading}>
          <Text color="neutral-600">Loading...</Text>
        </div>
      ) : (
        <BenchmarkGrid
          questions={questions}
          onEdit={setEditingQuestion}
          onDelete={setDeletingQuestion}
        />
      )}

      <Modal
        isOpen={!!editingQuestion}
        onClose={() => setEditingQuestion(null)}
        title="Edit Benchmark Question"
      >
        <BenchmarkQuestionForm
          initialValues={editingQuestion || undefined}
          onSubmit={handleUpdate}
          onCancel={() => setEditingQuestion(null)}
        />
      </Modal>

      <Modal
        isOpen={!!deletingQuestion}
        onClose={() => setDeletingQuestion(null)}
        title="Delete Benchmark Question"
        size="small"
      >
        <div className={styles.deleteConfirm}>
          <p>Are you sure you want to delete this benchmark question?</p>
          <div className={styles.buttons}>
            <Button variant="outline" onClick={() => setDeletingQuestion(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BenchmarkQuestionsPage;
